import { Grid, Stack, Typography } from '@mui/material';
import { InfoTypography } from './InfoRibbon';
import { PATH_DASHBOARD } from '../routes/paths';
import LaunchIcon from '@mui/icons-material/Launch';
import BadgeIcon from '@mui/icons-material/Badge';
import CakeIcon from '@mui/icons-material/Cake';
import { DATETIME_LIST_FORMAT, getFormattedDate } from 'src/careflow/common/annotation-utils';
import { isString } from 'lodash';
import { Reference } from 'src/careflow/ph-core/fhir-client/resources';
import { WrappedTask } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Task';
import { WrappedServiceRequest } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/ServiceRequest';
import { WrappedCondition } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Condition';
import { WrappedObservation } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Observation';
import { WrappedCarePlan } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/CarePlan';
import { WrappedPatient } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import CustomLink from 'src/careflow/common/CustomLink';

export type InfoRibbonPatientProps = {
  patient?: WrappedPatient | null;
  configurations: any;
  i18n?: any;
};

export type InfoRibbonServiceRequestProps = {
  serviceRequest?: WrappedServiceRequest | null;
  reference?: Reference | null;
  i18n?: any;
};

export type InfoRibbonConditionProps = {
  condition?: WrappedCondition | null;
};

export type InfoRibbonObservationProps = {
  observation?: WrappedObservation | null;
};

export type InfoRibbonCarePlanProps = {
  carePlan?: WrappedCarePlan | null;
  configurations: any;
  i18n: any;
};

export type InfoRibbonTaskProps = {
  task?: WrappedTask | null;
  configurations: any;
  i18n: any;
};

const getCodeFromResource = (resource: any) => {
  const coding = resource?.code?.coding?.[0];
  return coding?.code ?? '';
};

const getDisplayFromResource = (resource: any) => {
  const coding = resource?.code?.coding?.[0];
  return coding?.display ?? '';
};

const getCategoryTextFromResource = (resource: any) => resource?.category?.[0]?.text ?? '';

const smallFontSize = '0.9rem';
const defaultFontSize = '0.9rem';
const bigFontSize = '1.2rem';

const smallStyle = { fontSize: smallFontSize };
const defaultStyle = { fontSize: defaultFontSize };
const bigStyle = { fontSize: bigFontSize };

export const PrimaryTypography = ({ text, sx }: { text: any, sx?: any }) => (
  <InfoTypography
    containerSx={{
      ...defaultStyle,
      ...sx,
      // @ts-ignore
      color: (theme) => theme.palette.grey[500],
    }}
  >
    {text}
  </InfoTypography>
);

export const SecondaryTypography = ({ text, sx }: { text: any, sx?: any }) => (
  <InfoTypography
    containerSx={{
      ...defaultStyle,
      ...sx,
      // @ts-ignore
      color: (theme) => theme.palette.grey[900],
    }}
  >
    {text}
  </InfoTypography>
);

export const InfoRibbonPatient = ({ patient, configurations }: InfoRibbonPatientProps) => (
  <>
    <Grid container item xs={12}>
      <Stack direction="row" alignItems="center" gap={1}>
        <InfoTypography
          containerSx={{
            ...bigStyle,
            color: (theme) =>
              // @ts-ignore
              theme.palette.grey[600],
          }}
        >
          {patient?.getFullName?.() ?? ""}
        </InfoTypography>
        <CustomLink to={PATH_DASHBOARD.crs.patient.details.forId(patient?.id!)} target="_blank">
          <Typography noWrap variant="subtitle2">
            <LaunchIcon color="primary" />
          </Typography>
        </CustomLink>
      </Stack>
    </Grid>

    <Grid container item xs={12}>
      <Stack direction="row" alignItems="center" gap={3}>
        {!!patient?.getMRN?.(configurations?.systemMrn)?.value && (
          <Stack direction="row" alignItems="center" gap={1}>
            <BadgeIcon sx={{ color: (theme) => theme.palette.grey[600] }} />
            <InfoTypography containerSx={defaultStyle}>{patient.getMRN(configurations?.systemMrn)?.value}</InfoTypography>
          </Stack>
        )}
        {!!patient?.birthDate && (
          <Stack direction="row" alignItems="center" gap={1}>
            <CakeIcon sx={{ color: (theme) => theme.palette.grey[600] }} />
            <InfoTypography containerSx={{ ...defaultStyle, marginLeft: 'auto' }}>
              {patient?.getBirthDateForDisplay?.()} ({patient?.getAgeInYears?.()} years)
            </InfoTypography>
          </Stack>
        )}
      </Stack>
    </Grid>
  </>
);

export const InfoRibbonServiceRequest = ({ serviceRequest, i18n }: InfoRibbonServiceRequestProps) => {
  const defaultText = i18n ? i18n('patients.details.serviceRequests.titleModal', 'crs') : 'Service Request';
  return (
    <>
      <Grid container item xs={3}>
        <Grid item xs={12} alignContent={'center'}>
          <Stack direction="row" alignItems="center" gap={0}>
            <PrimaryTypography text={defaultText} />
          </Stack>
        </Grid>
      </Grid>

    <Grid container item xs={4}>
      <Grid container item xs={12}>
        <Stack direction="row" alignItems="center" gap={1}>
          <PrimaryTypography text={'From'} />
          <SecondaryTypography text={serviceRequest ? serviceRequest?.getRequesterDisplay() : ''} />
        </Stack>
      </Grid>
      <Grid container item xs={12}>
        <Stack direction="row" alignItems="center" gap={1}>
          <PrimaryTypography text={'To'} />
          <SecondaryTypography
            text={
              serviceRequest
                ? serviceRequest?.getPerformersByReferenceType?.('PractitionerRole')?.[0]?.display ||
                  serviceRequest?.getPerformersByReferenceType?.('Practitioner')?.[0]?.display ||
                  serviceRequest?.getPerformersByReferenceType?.('Organization')?.[0]?.display ||
                  ''
                : ''
            }
          />
        </Stack>
      </Grid>
    </Grid>

    <Grid container item xs={5}>
      <Grid container item xs={12}>
        <Stack direction="row" alignItems="center" gap={1}>
          <PrimaryTypography text={'Created'} />
          <SecondaryTypography
            text={
              serviceRequest?.authoredOn
                ? getFormattedDate(serviceRequest?.authoredOn, DATETIME_LIST_FORMAT)
                : ''
            }
          />
        </Stack>
      </Grid>
      <Grid container item xs={12}>
        <Stack direction="row" alignItems="center" gap={1}>
          <PrimaryTypography text={'Service'} />
          <SecondaryTypography
            text={
              serviceRequest
                ? serviceRequest?.getPerformersByReferenceType?.('HealthcareService')?.[0]?.display
                : ''
            }
          />
        </Stack>
      </Grid>
    </Grid>
  </>
  );
};

export const InfoRibbonObservation = ({ observation }: InfoRibbonObservationProps) => (
  <>
    <Grid container item xs={3}>
      <Grid item xs={12} alignContent={'center'}>
        <Stack direction="row" alignItems="center" gap={0}>
          <PrimaryTypography text={'Observation'} />
        </Stack>
      </Grid>
    </Grid>

    <Grid container item xs={9}>
      <Grid container item xs={12}>
        <Stack direction="row" alignItems="center" gap={1}>
          <PrimaryTypography text={'Category'} />
          <SecondaryTypography text={observation ? getCategoryTextFromResource(observation) : ''} />
        </Stack>
      </Grid>
      <Grid container item xs={12}>
        <Stack direction="row" alignItems="center" gap={1}>
          <PrimaryTypography text={'Type'} />
          <SecondaryTypography text={observation ? getDisplayFromResource(observation) : ''} />
        </Stack>
      </Grid>
    </Grid>
  </>
);

export const InfoRibbonCondition = ({ condition }: InfoRibbonConditionProps) => (
  <>
    <Grid container item xs={3}>
      <Grid item xs={12} alignContent={'center'}>
        <Stack direction="row" alignItems="center" gap={0}>
          <PrimaryTypography text={'Condition'} />
        </Stack>
      </Grid>
    </Grid>

    <Grid container item xs={9}>
      <Grid container item xs={12}>
        <Stack direction="row" alignItems="center" gap={1}>
          <PrimaryTypography text={'Code'} />
          <SecondaryTypography text={condition ? getCodeFromResource(condition) : ''} />
        </Stack>
      </Grid>
      <Grid container item xs={12}>
        <Stack direction="row" alignItems="center" gap={1}>
          <PrimaryTypography text={'RecordedDate'} />
          <SecondaryTypography
            text={
              condition && isString(condition?.recordedDate)
                ? getFormattedDate(condition?.recordedDate, DATETIME_LIST_FORMAT)
                : ''
            }
          />
        </Stack>
      </Grid>
    </Grid>
  </>
);

export const InfoRibbonCarePlan = ({ carePlan, configurations, i18n }: InfoRibbonCarePlanProps) => (
  <>
    <Grid container item xs={5}>
      <Grid item xs={12} alignContent={'center'}>
        <Stack direction="row" alignItems="center" gap={0}>
          <PrimaryTypography text={i18n('menu.case','crs')} sx={{ textOverflow: 'unset', whiteSpace: 'unset', textAlign: 'unset' }} />
        </Stack>
      </Grid>
    </Grid>

    <Grid container item xs={7} alignContent={'center'}>
      <Grid container item xs={12}>
        <Stack direction="row" alignItems="center" gap={1}>
          <PrimaryTypography text={'No.'} />
          <SecondaryTypography 
            text={
              carePlan ? 
                carePlan?.getIdentifierBySystem(configurations?.carePlanInternalNumberIdentifierSystem) 
                : ''
            } 
          />
        </Stack>
      </Grid>
      <Grid container item xs={12}>
        <Stack direction="row" alignItems="center" gap={1}>
          <PrimaryTypography text={'Status'} />
          <SecondaryTypography
            text={
              carePlan
                ? carePlan?.status
                : ''
            }
          />
        </Stack>
      </Grid>
    </Grid>
  </>
);

export const InfoRibbonPatientCustom = ({ patient, configurations, i18n }: InfoRibbonPatientProps) => (
  <>
    <Grid container item xs={3}>
      <Grid item xs={12} alignContent={'center'}>
        <Stack direction="row" alignItems="center" gap={0}>
          <PrimaryTypography text={i18n('menu.patient','crs')} />
        </Stack>
      </Grid>
    </Grid>

    <Grid container item xs={4}>
      <Grid container item xs={12}>
        <Stack direction="row" alignItems="center" gap={1}>
          <PrimaryTypography text={'Name'} />
          <SecondaryTypography 
            text={
              patient ? 
                patient?.getFullName()
                : ''
            } 
          />
        </Stack>
      </Grid>
      <Grid container item xs={12}>
        <Stack direction="row" alignItems="center" gap={1}>
          <PrimaryTypography text={'MRN'} />
          <SecondaryTypography
            text={
              patient ? 
                patient?.getMRN(configurations?.systemMrn)?.value
                : ''
            } 
          />
        </Stack>
      </Grid>
    </Grid>
  </>
);

export const InfoRibbonServiceRequestCustom = ({ reference, i18n }: InfoRibbonServiceRequestProps) => (
  <>
    <Grid container item xs={5}>
      <Grid item xs={12} alignContent={'center'}>
        <Stack direction="row" alignItems="center" gap={0}>
          <PrimaryTypography
            sx={{ textOverflow: 'unset', whiteSpace: 'unset', textAlign: 'unset' }}
            text={i18n('menu.inboundReferral','crs')} 
          />
        </Stack>
      </Grid>
    </Grid>

    <Grid container item xs={7}>
      <Grid container item xs={12}>
        <Stack direction="row" alignItems="center" gap={1}>
          <PrimaryTypography text={'No'} />
          <SecondaryTypography text={reference ? reference?.identifier?.value: ''} />
        </Stack>
      </Grid>
      <Grid container item xs={12}>
        <Stack direction="row" alignItems="center" gap={1}>
          <PrimaryTypography text={'Status'} />
          <SecondaryTypography
            text={
              reference
                ? reference?.display
                : ''
            }
          />
        </Stack>
      </Grid>
    </Grid>
  </>
);

export const InfoRibbonSubTask = ({ task, configurations, i18n }: InfoRibbonTaskProps) => (
  <>
    <Grid container item xs={5}>
      <Grid item xs={12} alignContent={'center'}>
        <Stack direction="row" alignItems="center" gap={0}>
          <PrimaryTypography text={i18n('menu.task','crs')} sx={{ textOverflow: 'unset', whiteSpace: 'unset', textAlign: 'unset' }} />
        </Stack>
      </Grid>
    </Grid>

    <Grid container item xs={7} alignContent={'center'}>
      <Grid container item xs={12}>
        <Stack direction="row" alignItems="center" gap={1}>
          <PrimaryTypography text={'No.'} />
          <SecondaryTypography 
            text={
              task ? 
              task?.getInternalNumber(configurations?.taskInternalNumberIdentifierSystem)
                : ''
            } 
          />
        </Stack>
      </Grid>
      <Grid container item xs={12}>
        <Stack direction="row" alignItems="center" gap={1}>
          <PrimaryTypography text={'Status'} />
          <SecondaryTypography
            text={
              task
                ? task?.status
                : ''
            }
          />
        </Stack>
      </Grid>
    </Grid>
  </>
);